import styles from './EquipmentCard.module.scss';
import classNames from 'classnames';
import { Picture } from 'ui/components';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { Button, Link, LinkButton } from 'ui/components/1-atoms';
import { Modal } from 'ui/components';

export interface EquipmentCardProps {
	className?: string;
	columnWidth: Models.GridCellWidth;
	equipment: Models.Equipment;
	imageSizes: string;
	isLoggedIn: boolean;
	hasBp: boolean;
}

export const EquipmentCardComponent: React.FC<EquipmentCardProps> = ({
	className,
	isLoggedIn,
	hasBp,
	...props
}) => {
	const { equipment, imageSizes } = props || {};
	const dictionary = useContext(DictionaryContext);
	const [flash, setFlash] = useState(false);
	const prev = useRef(equipment);
	const jobStatusKeyMap = {
		running: 'LabOverview.OperatingStateText',
		ready: 'LabOverview.IdleStateText',
		error: 'LabOverview.ErrorStateText',
	  };

	useEffect(() => {
		const changed =
			prev.current.serviceStatus !== equipment.serviceStatus ||
			prev.current.serviceContract !== equipment.serviceContract ||
			prev.current.serviceType !== equipment.serviceType;
			prev.current.jobStatus !== equipment.jobStatus;
			prev.current.jobName !== equipment.jobName;
			prev.current.hoursBeforeService !== equipment.hoursBeforeService;
			prev.current.jobErrorMessage !== equipment.jobErrorMessage;
			prev.current.jobStep !== equipment.jobStep;

		if (changed) {
			setFlash(true);
			const id = setTimeout(() => setFlash(false), 1000);
			return () => clearTimeout(id);
		}

		prev.current = equipment;

	}, [equipment]);
	return (
		<section className={classNames(styles.EquipmentCard, className)}>
			{equipment?.serviceContract && (
				// <svg className={styles.EquipmentCard_emblemIcon} width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
				// 	<path d="M0 0V13.3851C0 16.6124 1.1421 19.7243 3.21432 22.0806C6.1561 25.4262 10.2357 27.3254 11.9964 27.7378H12.005C13.7643 27.3254 17.8453 25.4276 20.7871 22.0806C22.8593 19.7243 24.0014 16.6109 24.0014 13.3851V0H0Z" fill="#95A3AB"/>
				// 	<path fill-rule="evenodd" clip-rule="evenodd" d="M10.2919 5.50863L3.44507 19.1071H6.88868L13.7067 5.50719L10.2919 5.50863Z" fill="white"/>
				// 	<path fill-rule="evenodd" clip-rule="evenodd" d="M16.288 2.10396L9.4397 15.7025L12.8833 15.7039L19.7013 2.10396H16.288Z" fill="white"/>
				// 	<path fill-rule="evenodd" clip-rule="evenodd" d="M17.143 8.93639L10.2947 22.5349L13.7383 22.5363L20.5563 8.93639H17.143Z" fill="white"/>
				// </svg>
				<svg className={styles.EquipmentCard_emblemIcon} width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0 0V13.3851C0 16.6124 1.1421 19.7243 3.21432 22.0806C6.1561 25.4262 10.2357 27.3254 11.9964 27.7378H12.005C13.7643 27.3254 17.8453 25.4276 20.7871 22.0806C22.8593 19.7243 24.0014 16.6109 24.0014 13.3851V0H0Z" fill="#95A3AB"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M10.2919 5.50863L3.44507 19.1071H6.88868L13.7067 5.50719L10.2919 5.50863Z" fill="white"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M16.288 2.10396L9.4397 15.7025L12.8833 15.7039L19.7013 2.10396H16.288Z" fill="white"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M17.143 8.93639L10.2947 22.5349L13.7383 22.5363L20.5563 8.93639H17.143Z" fill="white"/>
				</svg>
			)}
			<Link className={styles.EquipmentCard_link} aria-label={equipment?.name} url={'/equipment/' + equipment.sku}>
				<div className={styles.EquipmentCard_image}>
					<div className={styles.EquipmentCard_imageBox}>
						<Picture sizes={imageSizes} url={equipment?.picture} aspectRatio={0.75} className={styles.EquipmentCard_imageElement} />
					</div>
				</div>

				<div className={styles.EquipmentCard_details}>
					<p className={styles.EquipmentCard_category}>{equipment?.categories?.length && equipment?.categories[0]}</p>
					{/* {isLoggedIn && hasBp && (
						<p className={styles.EquipmentCard_meta}>
							{equipment?.historyLabel} {equipment?.historyValue}
						</p>
					)} */}
						<p className={styles.EquipmentCard_name}>{equipment?.name}</p>
						<p className={styles.EquipmentCard_serialnumber}>S/N: {equipment?.serialnumber}</p>					
					<div className={styles.EquipmentCard_details_bottom}>
						<div className={classNames(styles.EquipmentCard_details_bottom_status, {[styles.EquipmentCardFlash]: flash}
								)}>
							<span
								className={classNames(
								styles.EquipmentCard_details_bottom_statusBlock,
								styles[`EquipmentCard_details_bottom_statusBlock___${equipment?.jobStatus}`]
								)}
							></span>
							{dictionary.getValue(jobStatusKeyMap[equipment?.jobStatus?.toLowerCase()], null, equipment?.jobStatus )}
						</div>
						
						<span className={styles.EquipmentCard_details_bottom_statusLabel}>{dictionary.getValue('LabOverview.InfoLabel',	null,'Info',)}</span>	
						{equipment?.jobStatus === 'error' && (										
								<div className={classNames(
										styles.EquipmentCard_details_bottom_statusDetails,
										styles[`EquipmentCard_details_bottom_statusDetails___${equipment?.jobStatus}`],
											{
												[styles.EquipmentCard_details_bottom_statusDetails___withEmblem]: equipment?.serviceContract,
											}
										)}>
									{equipment?.jobErrorMessage}
								</div>						
							
						)}
						{equipment?.jobStatus === 'ready' && (								<>			
								<div className={classNames(
										styles.EquipmentCard_details_bottom_statusDetails,
										styles[`EquipmentCard_details_bottom_statusDetails___${equipment?.jobStatus}`],
											{
												[styles.EquipmentCard_details_bottom_statusDetails___withEmblem]: equipment?.serviceContract,
											}
										)}>
									{`Service due in ${equipment?.hoursBeforeService} hours`}
								</div>								
							</>
						)}
						{equipment?.jobStatus === 'running' && (	
							<>				
							<div className={classNames(
									styles.EquipmentCard_details_bottom_statusDetails,
									styles[`EquipmentCard_details_bottom_statusDetails___${equipment?.jobStatus}`],
										{
											[styles.EquipmentCard_details_bottom_statusDetails___withEmblem]: equipment?.serviceContract,
										}
									)}>
								{equipment?.jobName}
							</div>
							<div className={classNames(
								styles.EquipmentCard_details_bottom_statusDetails,
								styles[`EquipmentCard_details_bottom_statusDetails___${equipment?.jobStatus}`]
								)}>
								<svg className={styles.EquipmentCard_details_bottom_statusIcon} width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M6.5 4V6.5H9M12 6.5C12 9.53757 9.53757 12 6.5 12C3.46243 12 1 9.53757 1 6.5C1 3.46243 3.46243 1 6.5 1C9.53757 1 12 3.46243 12 6.5Z" stroke="#444444" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
								<span>{`${Math.floor(equipment?.elapsedTime / 60)}m`}</span>
								<span className={styles.EquipmentCard_details_bottom_statusStep}>{equipment?.jobStep}</span>
							</div>
							</>
						)}
						
					</div>
				</div>
			</Link>
		</section>
	);
};

// Updating data server polling
export const EquipmentCard = memo(EquipmentCardComponent, (prevProps, nextProps) => {
	const prev = prevProps.equipment;
	const next = nextProps.equipment;

	return (
		prev.guid === next.guid &&
		prev.serviceStatus === next.serviceStatus &&
		prev.serviceContract === next.serviceContract &&
		prev.serviceType === next.serviceType &&
		prev.hoursBeforeService === next.hoursBeforeService &&
		prev.jobStatus === next.jobStatus &&
		prev.jobName === next.jobName &&
		prev.jobStep === next.jobStep &&	
		prev.elapsedTime === next.elapsedTime &&
		prev.jobErrorMessage === next.jobErrorMessage
	);
});
  